import React, { useRef, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Lock, Download, Shuffle, Repeat, Repeat1, ListMusic, X } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { usePlayer, useAuth, API } from '@/App';
import { toast } from 'sonner';

// Constants for guest access limits
const GUEST_PREVIEW_DURATION = 34; // seconds
const GUEST_MAX_SONGS = 3;
const GUEST_PLAYS_STORAGE_KEY = '1samdigital_guest_plays';

const MusicPlayer = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { 
    currentTrack, isPlaying, volume, queue, shuffle, repeat,
    pauseTrack, resumeTrack, nextTrack, previousTrack, setVolume, 
    toggleShuffle, toggleRepeat, removeFromQueue, playTrack,
    isGuestLimited, setIsGuestLimited 
  } = usePlayer();
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [previewEnded, setPreviewEnded] = useState(false);
  const [guestPlaysCount, setGuestPlaysCount] = useState(0);
  const [showQueue, setShowQueue] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(() => {
    const saved = parseFloat(localStorage.getItem('1samdigital_playback_rate'));
    return [1, 1.25, 1.5, 2].includes(saved) ? saved : 1;
  });
  const lastSaveRef = useRef(0);
  const resumedTrackRef = useRef(null);

  const SPEEDS = [1, 1.25, 1.5, 2];
  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(playbackRate) + 1) % SPEEDS.length];
    setPlaybackRate(next);
    localStorage.setItem('1samdigital_playback_rate', String(next));
    toast.info(`Playback speed: ${next}x`);
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, currentTrack]);

  const isResumable = currentTrack && ['podcast', 'audiobook'].includes(currentTrack.content_type);

  const saveProgress = (pos) => {
    if (!token || !currentTrack || !['podcast', 'audiobook'].includes(currentTrack.content_type)) return;
    fetch(`${API}/tracks/${currentTrack.id}/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ position: pos, duration: audioRef.current?.duration || duration || 0 })
    }).catch(() => {});
  };

  // Check if user is a guest (not logged in)
  const isGuest = !user;
  
  // Check if user can download (premium only)
  const canDownload = user?.subscription_status === 'premium';

  // Load guest plays count from localStorage
  useEffect(() => {
    if (isGuest) {
      const storedPlays = localStorage.getItem(GUEST_PLAYS_STORAGE_KEY);
      if (storedPlays) {
        const plays = JSON.parse(storedPlays);
        setGuestPlaysCount(plays.length);
      }
    }
  }, [isGuest]);

  // Track guest plays
  const trackGuestPlay = (trackId) => {
    if (!isGuest) return true;
    
    const storedPlays = localStorage.getItem(GUEST_PLAYS_STORAGE_KEY);
    let plays = storedPlays ? JSON.parse(storedPlays) : [];
    
    // Update state with current count
    setGuestPlaysCount(plays.length);
    
    // Check if this track was already played
    if (!plays.includes(trackId)) {
      if (plays.length >= GUEST_MAX_SONGS) {
        // Limit reached - show signup prompt
        setShowSignupPrompt(true);
        return false;
      }
      plays.push(trackId);
      localStorage.setItem(GUEST_PLAYS_STORAGE_KEY, JSON.stringify(plays));
      setGuestPlaysCount(plays.length);
    }
    return true;
  };

  useEffect(() => {
    if (audioRef.current && currentTrack) {
      // Check guest limit before playing
      if (isGuest) {
        const canPlay = trackGuestPlay(currentTrack.id);
        if (!canPlay) {
          pauseTrack();
          return;
        }
      }
      
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
        setPreviewEnded(false);
      } else {
        audioRef.current.pause();
        if (isResumable && token) {
          saveProgress(audioRef.current.currentTime);
        }
      }
    }
  }, [isPlaying, currentTrack]);

  // Reset progress-save tracking and save position when switching tracks
  useEffect(() => {
    lastSaveRef.current = 0;
    const audio = audioRef.current;
    const track = currentTrack;
    return () => {
      if (audio && token && track && ['podcast', 'audiobook'].includes(track.content_type) && audio.currentTime > 0) {
        fetch(`${API}/tracks/${track.id}/progress`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ position: audio.currentTime, duration: audio.duration || 0 })
        }).catch(() => {});
      }
    };
  }, [currentTrack?.id]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const time = audioRef.current.currentTime;
      setCurrentTime(time);
      
      // Periodically save progress for podcasts/audiobooks
      if (isResumable && token && Math.abs(time - lastSaveRef.current) >= 15) {
        lastSaveRef.current = time;
        saveProgress(time);
      }
      
      // Guest preview limit - pause at 34 seconds
      if (isGuest && time >= GUEST_PREVIEW_DURATION && !previewEnded) {
        audioRef.current.pause();
        pauseTrack();
        setPreviewEnded(true);
        
        // Check if max songs reached
        const storedPlays = localStorage.getItem(GUEST_PLAYS_STORAGE_KEY);
        const plays = storedPlays ? JSON.parse(storedPlays) : [];
        
        if (plays.length >= GUEST_MAX_SONGS) {
          // Show signup dialog when limit reached
          setShowSignupPrompt(true);
        } else {
          // Show signup dialog even for non-final previews to encourage signup
          setShowSignupPrompt(true);
        }
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      audioRef.current.playbackRate = playbackRate;
      
      // Resume podcasts/audiobooks from last saved position
      const dur = audioRef.current.duration;
      if (isResumable && token && resumedTrackRef.current !== currentTrack.id) {
        resumedTrackRef.current = currentTrack.id;
        fetch(`${API}/tracks/${currentTrack.id}/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(r => (r.ok ? r.json() : null))
          .then(data => {
            if (data && data.position > 10 && data.position < dur * 0.95 && audioRef.current) {
              audioRef.current.currentTime = data.position;
              setCurrentTime(data.position);
              toast.info(`Resumed from ${formatTime(data.position)}`);
            }
          })
          .catch(() => {});
      }
    }
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      // Guests can only seek within preview duration
      const seekTo = isGuest ? Math.min(value[0], GUEST_PREVIEW_DURATION) : value[0];
      audioRef.current.currentTime = seekTo;
      setCurrentTime(seekTo);
    }
  };

  const handleEnded = () => {
    // Mark podcast/audiobook as finished (clears saved progress server-side)
    if (isResumable && token) {
      saveProgress(audioRef.current?.duration || duration);
    }
    
    if (isGuest) {
      // Check if more songs available for preview
      const storedPlays = localStorage.getItem(GUEST_PLAYS_STORAGE_KEY);
      const plays = storedPlays ? JSON.parse(storedPlays) : [];
      
      if (plays.length >= GUEST_MAX_SONGS) {
        setShowSignupPrompt(true);
        return;
      }
    }
    
    // Handle repeat modes
    if (repeat === 'one') {
      // Repeat single track
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
      return;
    }
    
    const result = nextTrack();
    if (result === 'end' && repeat === 'off') {
      pauseTrack();
    }
  };

  const handleDownload = async () => {
    if (!canDownload) {
      toast.error('Download is available for Premium subscribers only');
      return;
    }
    
    if (!currentTrack) return;
    
    try {
      // Record the download on backend
      await fetch(`${API}/tracks/${currentTrack.id}/download`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Trigger download - for PWA, we'll cache it
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        // Send message to service worker to cache this track
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_TRACK',
          track: currentTrack
        });
        toast.success('Track saved for offline listening!');
      } else {
        // Fallback: direct download
        const link = document.createElement('a');
        link.href = currentTrack.audio_url;
        link.download = `${currentTrack.title} - ${currentTrack.creator_username}.mp3`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Download started!');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download track');
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  // Calculate effective duration for progress bar (guests see limited duration)
  const effectiveDuration = isGuest ? Math.min(duration, GUEST_PREVIEW_DURATION) : duration;

  if (!currentTrack) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          className="player-bar"
          data-testid="music-player"
        >
          <audio
            ref={audioRef}
            src={currentTrack.audio_url}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
          />

          {/* Progress Bar */}
          <div className="h-1 bg-white/10 relative">
            {/* Guest preview indicator */}
            {isGuest && duration > GUEST_PREVIEW_DURATION && (
              <div 
                className="absolute top-0 h-full bg-white/5"
                style={{ 
                  left: `${(GUEST_PREVIEW_DURATION / duration) * 100}%`,
                  right: 0
                }}
              >
                <div className="absolute left-0 top-0 h-full w-0.5 bg-yellow-500/50"></div>
              </div>
            )}
            <Slider
              value={[Math.min(currentTime, effectiveDuration)]}
              max={effectiveDuration || 100}
              step={0.1}
              onValueChange={handleSeek}
              className="h-1 cursor-pointer"
              data-testid="player-progress"
            />
          </div>

          <div className="px-3 sm:px-4 md:container-main py-2 sm:py-3 md:py-4">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              {/* Track Info - Mobile Optimized */}
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg overflow-hidden flex-shrink-0 relative">
                  {currentTrack.artwork_url ? (
                    <img 
                      src={currentTrack.artwork_url}
                      alt={currentTrack.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#7000FF] to-[#00F0FF]"></div>
                  )}
                  {/* Guest preview badge */}
                  {isGuest && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <span className="text-[8px] font-bold text-yellow-400 bg-black/60 px-1 rounded">
                        PREVIEW
                      </span>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <Link 
                    to={`/track/${currentTrack.id}`}
                    className="font-medium text-sm sm:text-base truncate block hover:text-[#7000FF] transition-colors"
                    data-testid="player-track-title"
                  >
                    {currentTrack.title}
                  </Link>
                  <div className="flex items-center gap-2">
                    <Link 
                      to={`/profile/${currentTrack.creator_id}`}
                      className="text-white/50 text-xs sm:text-sm truncate hover:text-white/70 transition-colors"
                      data-testid="player-track-artist"
                    >
                      {currentTrack.creator_username}
                    </Link>
                    {isGuest && (
                      <span className="text-yellow-500 text-xs hidden sm:inline">
                        • {GUEST_MAX_SONGS - guestPlaysCount} preview{guestPlaysCount === GUEST_MAX_SONGS - 1 ? '' : 's'} left
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Time - Mobile: Compact */}
              <span className="font-mono text-xs text-white/50 hidden xs:block sm:hidden">
                {formatTime(currentTime)}
                {isGuest && <span className="text-yellow-500">/{formatTime(GUEST_PREVIEW_DURATION)}</span>}
              </span>

              {/* Controls - Mobile Optimized */}
              <div className="flex items-center gap-1 sm:gap-2 md:gap-3">
                {/* Shuffle - Desktop only */}
                {!isGuest && (
                  <button
                    onClick={toggleShuffle}
                    className={`p-1.5 sm:p-2 transition-colors hidden md:block ${shuffle ? 'text-[#7000FF]' : 'text-white/40 hover:text-white/60'}`}
                    title={shuffle ? 'Shuffle On' : 'Shuffle Off'}
                  >
                    <Shuffle className="w-4 h-4" />
                  </button>
                )}
                
                {/* Previous Track */}
                {!isGuest && (
                  <button
                    onClick={previousTrack}
                    className="p-1.5 sm:p-2 text-white/60 hover:text-white transition-colors hidden sm:block"
                    title="Previous"
                  >
                    <SkipBack className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}
                
                {/* Play/Pause */}
                <button
                  onClick={isPlaying ? pauseTrack : resumeTrack}
                  className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform flex-shrink-0"
                  data-testid="player-play-btn"
                  disabled={previewEnded && isGuest}
                >
                  {isPlaying ? (
                    <Pause className="w-4 h-4 sm:w-5 sm:h-5 text-black" fill="black" />
                  ) : (
                    <Play className="w-4 h-4 sm:w-5 sm:h-5 text-black ml-0.5" fill="black" />
                  )}
                </button>
                
                {/* Next Track */}
                <button
                  onClick={() => nextTrack()}
                  className="p-1.5 sm:p-2 text-white/60 hover:text-white transition-colors hidden sm:block"
                  data-testid="player-next-btn"
                  title="Next"
                >
                  <SkipForward className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                
                {/* Playback speed - podcasts & audiobooks */}
                {isResumable && (
                  <button
                    onClick={cycleSpeed}
                    className="px-2 py-1 rounded-md text-xs font-mono font-bold text-white/70 hover:text-white bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0"
                    title="Playback speed"
                    data-testid="player-speed-btn"
                  >
                    {playbackRate}x
                  </button>
                )}
                
                {/* Repeat - Desktop only */}
                {!isGuest && (
                  <button
                    onClick={toggleRepeat}
                    className={`p-1.5 sm:p-2 transition-colors hidden md:block ${repeat !== 'off' ? 'text-[#7000FF]' : 'text-white/40 hover:text-white/60'}`}
                    title={repeat === 'off' ? 'Repeat Off' : repeat === 'all' ? 'Repeat All' : 'Repeat One'}
                  >
                    {repeat === 'one' ? <Repeat1 className="w-4 h-4" /> : <Repeat className="w-4 h-4" />}
                  </button>
                )}
                
                {/* Queue button - Desktop only */}
                {!isGuest && queue.length > 0 && (
                  <button
                    onClick={() => setShowQueue(!showQueue)}
                    className={`p-1.5 sm:p-2 transition-colors hidden md:block relative ${showQueue ? 'text-[#7000FF]' : 'text-white/40 hover:text-white/60'}`}
                    title="Queue"
                  >
                    <ListMusic className="w-4 h-4" />
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#7000FF] rounded-full text-[10px] flex items-center justify-center">
                      {queue.length}
                    </span>
                  </button>
                )}
                
                {/* Download button for premium users */}
                {canDownload && (
                  <button
                    onClick={handleDownload}
                    className="p-1.5 sm:p-2 text-[#00F0FF] hover:text-[#00F0FF]/80 transition-colors hidden md:block"
                    title="Download for offline"
                  >
                    <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}
              </div>

              {/* Time & Volume - Desktop */}
              <div className="hidden md:flex items-center gap-4">
                <span className="font-mono text-sm text-white/50 w-24 text-center">
                  {formatTime(currentTime)} / {formatTime(isGuest ? GUEST_PREVIEW_DURATION : duration)}
                  {isGuest && currentTime >= GUEST_PREVIEW_DURATION && (
                    <Lock className="inline w-3 h-3 ml-1 text-yellow-500" />
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={toggleMute} className="text-white/60 hover:text-white">
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <Slider
                    value={[isMuted ? 0 : volume * 100]}
                    max={100}
                    step={1}
                    onValueChange={(value) => {
                      setVolume(value[0] / 100);
                      setIsMuted(false);
                    }}
                    className="w-24"
                    data-testid="player-volume"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Signup Prompt Dialog */}
      <Dialog open={showSignupPrompt} onOpenChange={setShowSignupPrompt}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Lock className="w-5 h-5 text-[#7000FF]" />
              Create Your Free Account
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {guestPlaysCount >= GUEST_MAX_SONGS 
                ? `You've previewed ${GUEST_MAX_SONGS} songs! Sign up for free to unlock unlimited streaming.`
                : `Preview ended! You have ${GUEST_MAX_SONGS - guestPlaysCount} preview${GUEST_MAX_SONGS - guestPlaysCount === 1 ? '' : 's'} left. Sign up for free to listen to full songs.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-white/5 rounded-lg p-4 space-y-3">
              <p className="font-medium text-[#00F0FF]">Free Account Includes:</p>
              <ul className="space-y-2 text-sm text-white/70">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Unlimited song streaming
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Support artists with tips
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Create playlists
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span> Follow your favorite creators
                </li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-r from-[#7000FF]/20 to-[#00F0FF]/20 rounded-lg p-4">
              <p className="font-medium mb-1">Want more?</p>
              <p className="text-sm text-white/60">
                Premium subscribers get ad-free listening, offline downloads, and exclusive content!
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {guestPlaysCount < GUEST_MAX_SONGS && (
              <Button 
                variant="outline" 
                onClick={() => setShowSignupPrompt(false)}
                className="w-full sm:w-auto"
              >
                Continue Previewing
              </Button>
            )}
            <Button 
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto btn-primary"
            >
              Create Free Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Queue Panel */}
      <AnimatePresence>
        {showQueue && queue.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-24 right-4 w-80 max-h-96 bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden shadow-2xl z-50"
          >
            <div className="p-4 border-b border-white/10 flex justify-between items-center">
              <h3 className="font-heading font-bold flex items-center gap-2">
                <ListMusic className="w-5 h-5 text-[#7000FF]" />
                Up Next ({queue.length})
              </h3>
              <button onClick={() => setShowQueue(false)} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {queue.map((track, index) => (
                <div
                  key={`queue-${track.id}-${index}`}
                  className="p-3 hover:bg-white/5 flex items-center gap-3 group"
                >
                  <span className="text-white/30 text-sm w-4">{index + 1}</span>
                  <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                    {track.artwork_url ? (
                      <img src={track.artwork_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#7000FF] to-[#00F0FF]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{track.title}</p>
                    <p className="text-xs text-white/40 truncate">{track.creator_username}</p>
                  </div>
                  <button
                    onClick={() => removeFromQueue(index)}
                    className="p-1 text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MusicPlayer;
